/**
 * Taal van de gepubliceerde offerte (UI-teksten). Inhoud van de offerte zelf blijft zoals geschreven.
 */

export type PublicQuoteLocale = 'nl' | 'en';

export function normalizePublicQuoteLocale(v: unknown): PublicQuoteLocale {
  return v === 'en' ? 'en' : 'nl';
}

export function publicDateLocaleTag(locale: PublicQuoteLocale): string {
  return locale === 'en' ? 'en-GB' : 'nl-NL';
}

export function formatPublicDate(
  locale: PublicQuoteLocale,
  d: Date | string | number,
  opts?: Intl.DateTimeFormatOptions,
): string {
  const date = typeof d === 'string' || typeof d === 'number' ? new Date(d) : d;
  return date.toLocaleDateString(
    publicDateLocaleTag(locale),
    opts ?? { day: '2-digit', month: '2-digit', year: 'numeric' },
  );
}

export function formatPublicDateTime(locale: PublicQuoteLocale, d: Date | string | number): string {
  const date = typeof d === 'string' || typeof d === 'number' ? new Date(d) : d;
  return date.toLocaleString(publicDateLocaleTag(locale), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export interface PublicQuoteStrings {
  page: {
    loading: string;
    passwordTitle: string;
    passwordDescription: string;
    passwordLabel: string;
    passwordInvalid: string;
    passwordSubmit: string;
    errorExpired: string;
    expiredTitle: string;
    expiredBody: string;
    expiredContact: string;
    errorNotFoundTitle: string;
    errorNotFoundBody: string;
    pageLabel: string;
    pageOfWord: string;
    signedBadge: string;
    acceptedBadge: string;
    declinedBadge: string;
    pdf: string;
    /** Tijdens server-PDF-generatie (publieke pagina) */
    pdfGenerating: string;
    signThisQuote: string;
    decline: string;
    accept: string;
    ctaAwaitingSign: string;
    ctaReady: string;
    acceptQuote: string;
    /** Meerdere tekenbevoegden: '{remaining}' resterend */
    ctaAwaitingOtherSigners: string;
    /** Concept / draft: vaste banner op publieke pagina */
    draftBanner: string;
    /** Toelichting bij uitgeschakelde PDF-knop */
    draftPdfDisabled: string;
  };
  respondDialog: {
    acceptTitle: string;
    declineTitle: string;
    acceptIntro: string;
    declineIntro: string;
    nameLabel: string;
    remarkOptional: string;
    declineReasonOptional: string;
    cancel: string;
    busy: string;
    accept: string;
    decline: string;
  };
  boxSign: {
    title: string;
    iframeTitle: string;
    loading: string;
  };
  videoIntro: {
    title: string;
    iframeTitle: string;
    openInTab: string;
    viewQuote: string;
    replayTitle: string;
    replayAria: string;
  };
  hook: {
    expired: string;
    withdrawn: string;
    loadFailed: string;
    notFound: string;
    signedSuccess: string;
    declinedInfo: string;
    pdfFailed: string;
    signFirst: string;
    acceptedSuccess: string;
    declinedToast: string;
    actionFailed: string;
    /** Toast bij download-poging in conceptmodus */
    draftNoDownload: string;
    /** Getekend, maar er ontbreken nog tekenbevoegden */
    signedPartially: string;
  };
  signature: {
    clientSignature: string;
    /** Meerdere tekenbevoegden: '{n}' volgnummer */
    clientSignatureNumbered: string;
    ubo: string;
    signed: string;
    onDate: string;
    acceptedNoSign: string;
    declinedQuote: string;
    acceptLoading: string;
    acceptButton: string;
    altRecipient: string;
    altSender: string;
    signedAfterClient: string;
    /** Nog niet getekend vak van een andere tekenbevoegde */
    notSignedYet: string;
    signButton: string;
    /** '{signed}' van '{total}' tekenbevoegden */
    signProgress: string;
    signDialogTitle: string;
    /** '{name}' van de tekenbevoegde */
    signDialogIntro: string;
    signDialogSubmit: string;
    signDialogCancel: string;
    signStatus: {
      signed: string;
      declined: string;
      waiting: string;
    };
    clientLabel: string;
    /** Handtekenpagina bij status concept (publiek) */
    draftPreviewNote: string;
  };
  /** Handtekening-canvas (SignatureCanvas): knoppen en placeholders */
  signatureCanvas: {
    draw: string;
    type: string;
    undo: string;
    clear: string;
    namePlaceholder: string;
    drawHint: string;
  };
  countersign: {
    invalidLink: string;
    loadFailed: string;
    loading: string;
    cannotLoadTitle: string;
    headerTitle: string;
    coSignedBadge: string;
    infoLine: string;
    infoSub: string;
    sectionTitle: string;
    sectionIntro: string;
    doneTitle: string;
    submitting: string;
    submit: string;
    toastSuccess: string;
    toastFail: string;
    fallbackClient: string;
  };
}

const NL: PublicQuoteStrings = {
  page: {
    loading: 'Offerte laden...',
    passwordTitle: 'Wachtwoord vereist',
    passwordDescription: 'Deze offerte is beveiligd met een wachtwoord.',
    passwordLabel: 'Wachtwoord',
    passwordInvalid: 'Ongeldig wachtwoord',
    passwordSubmit: 'Bekijk offerte',
    errorExpired: 'Deze offerte is verlopen',
    expiredTitle: 'Offerte verlopen',
    expiredBody: 'Deze offerte is niet meer beschikbaar. De geldigheidsduur is verstreken.',
    expiredContact: 'Neem contact op met de afzender om een nieuwe offerte aan te vragen.',
    errorNotFoundTitle: 'Offerte niet gevonden',
    errorNotFoundBody: 'Neem contact op met de afzender als dit onverwacht is.',
    pageLabel: 'Pagina',
    pageOfWord: 'van',
    signedBadge: 'Ondertekend',
    acceptedBadge: 'Geaccepteerd',
    declinedBadge: 'Afgewezen',
    pdf: 'PDF',
    pdfGenerating: 'PDF wordt gemaakt…',
    signThisQuote: 'Onderteken deze offerte',
    decline: 'Afwijzen',
    accept: 'Accepteren',
    ctaAwaitingSign: 'Deze offerte wacht op uw digitale handtekening.',
    ctaReady: 'Klaar om te reageren op deze offerte?',
    acceptQuote: 'Offerte accepteren',
    ctaAwaitingOtherSigners:
      'Nog {remaining} tekenbevoegde(n) moeten ondertekenen voordat de offerte definitief is.',
    draftBanner:
      'Concept — niet voor de klant. Accepteren, ondertekenen en PDF-download zijn uitgeschakeld tot de offerte is gepubliceerd.',
    draftPdfDisabled: 'PDF-download is bij een concept niet beschikbaar.',
  },
  respondDialog: {
    acceptTitle: 'Offerte accepteren',
    declineTitle: 'Offerte afwijzen',
    acceptIntro: 'Door te accepteren gaat u akkoord met de voorwaarden in deze offerte.',
    declineIntro: 'Laat ons weten waarom u de offerte afwijst.',
    nameLabel: 'Uw naam',
    remarkOptional: 'Opmerking (optioneel)',
    declineReasonOptional: 'Reden voor afwijzing (optioneel)',
    cancel: 'Annuleren',
    busy: 'Bezig...',
    accept: 'Accepteren',
    decline: 'Afwijzen',
  },
  boxSign: {
    title: 'Document ondertekenen',
    iframeTitle: 'Box Sign - Document ondertekenen',
    loading: 'Ondertekeningsformulier laden...',
  },
  videoIntro: {
    title: 'Persoonlijke videoboodschap',
    iframeTitle: 'Videoboodschap',
    openInTab: 'Video openen in nieuw tabblad',
    viewQuote: 'Bekijk de offerte',
    replayTitle: 'Videoboodschap opnieuw bekijken',
    replayAria: 'Videoboodschap opnieuw bekijken',
  },
  hook: {
    expired: 'Deze offerte is verlopen',
    withdrawn: 'Deze offerte is ingetrokken en niet meer beschikbaar',
    loadFailed:
      'Kan de offerte niet laden. Probeer het later opnieuw of neem contact op met de afzender als dit blijft gebeuren.',
    notFound: 'Offerte niet gevonden',
    signedSuccess: 'Document succesvol ondertekend!',
    declinedInfo: 'Ondertekening is afgewezen.',
    pdfFailed: 'PDF downloaden mislukt',
    signFirst: 'Plaats eerst uw handtekening voordat u de offerte accepteert.',
    acceptedSuccess: 'Offerte geaccepteerd! We nemen contact met u op.',
    declinedToast: 'Offerte afgewezen.',
    actionFailed: 'Actie mislukt. Probeer het opnieuw.',
    draftNoDownload: 'PDF-download is voor concept-offertes uitgeschakeld.',
    signedPartially:
      'Handtekening opgeslagen. De offerte is definitief zodra alle tekenbevoegden hebben getekend.',
  },
  signature: {
    clientSignature: 'Handtekening klant',
    clientSignatureNumbered: 'Tekenbevoegde {n}',
    ubo: 'UBO',
    signed: 'Ondertekend',
    onDate: 'op',
    acceptedNoSign: 'Offerte geaccepteerd',
    declinedQuote: 'Offerte afgewezen',
    acceptLoading: 'Bezig met accepteren...',
    acceptButton: 'Offerte accepteren',
    altRecipient: 'Handtekening',
    altSender: 'UBO-handtekening',
    signedAfterClient: 'Wordt ondertekend na klant',
    notSignedYet: 'Nog niet ondertekend',
    signButton: 'Ondertekenen',
    signProgress: '{signed} van {total} tekenbevoegden hebben getekend',
    signDialogTitle: 'Offerte ondertekenen',
    signDialogIntro: 'Plaats hieronder de handtekening van {name}.',
    signDialogSubmit: 'Ondertekenen',
    signDialogCancel: 'Annuleren',
    signStatus: {
      signed: 'Ondertekend',
      declined: 'Afgewezen',
      waiting: 'Wacht op ondertekening',
    },
    clientLabel: 'Klant',
    draftPreviewNote:
      'Dit is een conceptofferte. Handtekenen en accepteren zijn uitgeschakeld tot de offerte is gepubliceerd.',
  },
  signatureCanvas: {
    draw: 'Tekenen',
    type: 'Typen',
    undo: 'Ongedaan maken',
    clear: 'Wissen',
    namePlaceholder: 'Typ uw naam',
    drawHint: 'Teken hier uw handtekening',
  },
  countersign: {
    invalidLink: 'Ongeldige link',
    loadFailed: 'Kan offerte niet laden',
    loading: 'Offerte laden...',
    cannotLoadTitle: 'Kan niet laden',
    headerTitle: 'Medeondertekening',
    coSignedBadge: 'Medeondertekend',
    infoLine: '{sender}, de klant ({client}) heeft deze offerte ondertekend.',
    infoSub: 'Controleer de offerte hieronder en plaats je medeondertekening onderaan.',
    sectionTitle: 'Medeondertekening — {sender}',
    sectionIntro: 'Plaats hieronder je handtekening om de offerte namens UBO te medeondertekenen.',
    doneTitle: 'Offerte is medeondertekend',
    submitting: 'Bezig met ondertekenen...',
    submit: 'Medeondertekenen',
    toastSuccess: 'Offerte medeondertekend!',
    toastFail: 'Medeondertekening mislukt',
    fallbackClient: 'Klant',
  },
};

const EN: PublicQuoteStrings = {
  page: {
    loading: 'Loading quote...',
    passwordTitle: 'Password required',
    passwordDescription: 'This quote is protected with a password.',
    passwordLabel: 'Password',
    passwordInvalid: 'Invalid password',
    passwordSubmit: 'View quote',
    errorExpired: 'This quote has expired',
    expiredTitle: 'Quote expired',
    expiredBody: 'This quote is no longer available. The validity period has passed.',
    expiredContact: 'Contact the sender to request a new quote.',
    errorNotFoundTitle: 'Quote not found',
    errorNotFoundBody: 'Contact the sender if this is unexpected.',
    pageLabel: 'Page',
    pageOfWord: 'of',
    signedBadge: 'Signed',
    acceptedBadge: 'Accepted',
    declinedBadge: 'Declined',
    pdf: 'PDF',
    pdfGenerating: 'Creating PDF…',
    signThisQuote: 'Sign this quote',
    decline: 'Decline',
    accept: 'Accept',
    ctaAwaitingSign: 'This quote is waiting for your electronic signature.',
    ctaReady: 'Ready to respond to this quote?',
    acceptQuote: 'Accept quote',
    ctaAwaitingOtherSigners: '{remaining} more signatory/signatories must sign before the quote is final.',
    draftBanner:
      'Draft — not for the customer. Accept, sign and PDF download are disabled until the quote is published.',
    draftPdfDisabled: 'PDF download is not available for draft quotes.',
  },
  respondDialog: {
    acceptTitle: 'Accept quote',
    declineTitle: 'Decline quote',
    acceptIntro: 'By accepting, you agree to the terms in this quote.',
    declineIntro: 'Let us know why you are declining the quote.',
    nameLabel: 'Your name',
    remarkOptional: 'Comment (optional)',
    declineReasonOptional: 'Reason for declining (optional)',
    cancel: 'Cancel',
    busy: 'Please wait...',
    accept: 'Accept',
    decline: 'Decline',
  },
  boxSign: {
    title: 'Sign document',
    iframeTitle: 'Box Sign — Sign document',
    loading: 'Loading signing form...',
  },
  videoIntro: {
    title: 'Personal video message',
    iframeTitle: 'Video message',
    openInTab: 'Open video in new tab',
    viewQuote: 'View the quote',
    replayTitle: 'Replay video message',
    replayAria: 'Replay video message',
  },
  hook: {
    expired: 'This quote has expired',
    withdrawn: 'This quote has been withdrawn and is no longer available',
    loadFailed:
      'Could not load the quote. Try again later or contact the sender if the problem persists.',
    notFound: 'Quote not found',
    signedSuccess: 'Document signed successfully!',
    declinedInfo: 'Signing was declined.',
    pdfFailed: 'PDF download failed',
    signFirst: 'Add your signature before accepting the quote.',
    acceptedSuccess: 'Quote accepted! We will be in touch.',
    declinedToast: 'Quote declined.',
    actionFailed: 'Something went wrong. Please try again.',
    draftNoDownload: 'PDF download is disabled for draft quotes.',
    signedPartially: 'Signature saved. The quote is final once every signatory has signed.',
  },
  signature: {
    clientSignature: 'Client signature',
    clientSignatureNumbered: 'Signatory {n}',
    ubo: 'UBO',
    signed: 'Signed',
    onDate: 'on',
    acceptedNoSign: 'Quote accepted',
    declinedQuote: 'Quote declined',
    acceptLoading: 'Accepting...',
    acceptButton: 'Accept quote',
    altRecipient: 'Signature',
    altSender: 'UBO signature',
    signedAfterClient: 'Will be signed after the client',
    notSignedYet: 'Not signed yet',
    signButton: 'Sign',
    signProgress: '{signed} of {total} signatories have signed',
    signDialogTitle: 'Sign quote',
    signDialogIntro: 'Add the signature of {name} below.',
    signDialogSubmit: 'Sign',
    signDialogCancel: 'Cancel',
    signStatus: {
      signed: 'Signed',
      declined: 'Declined',
      waiting: 'Awaiting signature',
    },
    clientLabel: 'Client',
    draftPreviewNote:
      'This is a draft quote. Signing and accepting are disabled until the quote is published.',
  },
  signatureCanvas: {
    draw: 'Draw',
    type: 'Type',
    undo: 'Undo',
    clear: 'Clear',
    namePlaceholder: 'Type your name',
    drawHint: 'Draw your signature here',
  },
  countersign: {
    invalidLink: 'Invalid link',
    loadFailed: 'Could not load quote',
    loading: 'Loading quote...',
    cannotLoadTitle: 'Could not load',
    headerTitle: 'Co-sign',
    coSignedBadge: 'Co-signed',
    infoLine: '{sender}, the client ({client}) has signed this quote.',
    infoSub: 'Review the quote below and add your co-signature at the bottom.',
    sectionTitle: 'Co-signature — {sender}',
    sectionIntro: 'Add your signature below to co-sign this quote on behalf of UBO.',
    doneTitle: 'Quote has been co-signed',
    submitting: 'Signing...',
    submit: 'Co-sign',
    toastSuccess: 'Quote co-signed!',
    toastFail: 'Co-signing failed',
    fallbackClient: 'Client',
  },
};

const MAP: Record<PublicQuoteLocale, PublicQuoteStrings> = { nl: NL, en: EN };

export function getPublicQuoteStrings(locale: PublicQuoteLocale): PublicQuoteStrings {
  return MAP[locale] ?? NL;
}
